import { useState } from "react";
import { useMutation } from "@apollo/client";
import { GENERATE_EVIDENCE_DOWNLOAD_SIGNED_URL } from "../../../../../apis/organization-apis/evidence-apis/evidenceApis";
import { downloadFileWhenReady } from "../../../../../utils/downloadUtils";

/**
 * Custom hook to handle bulk evidence download using zip pattern
 * @returns {Object} Download state, handler function, and error state
 */
export const useBulkDownload = () => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [generateEvidenceDownloadSignedUrl] = useMutation(GENERATE_EVIDENCE_DOWNLOAD_SIGNED_URL);

  const downloadEvidenceFiles = async (selectedEmployees, filteredEmployees) => {
    if (selectedEmployees.length === 0) return;

    setIsDownloading(true);
    setDownloadError(null);

    try {
      // Collect all evidence IDs for selected employees
      const allEvidenceIds = [];
      selectedEmployees.forEach((employeeId) => {
        const employee = filteredEmployees.find((emp) => emp.id === employeeId);

        if (employee && employee.evidenceItems) {
          employee.evidenceItems.forEach((evidence) => {
            if (evidence.id) {
              allEvidenceIds.push(parseInt(evidence.id));
            }
          });
        }
      });

      if (allEvidenceIds.length === 0) {
        setDownloadError("No evidence files found for selected employees");
        return;
      }

      // Generate signed URL for zip download
      const result = await generateEvidenceDownloadSignedUrl({
        variables: {
          evidence_ids: allEvidenceIds,
        },
      });

      const url = result?.data?.generateEvidenceDownloadSignedUrl?.url;
      if (!url) {
        setDownloadError("Download failed: no URL returned");
        return;
      }

      await downloadFileWhenReady(url, "evidences.zip");
    } catch (error) {
      console.error("Error downloading evidence files:", error);
      setDownloadError(error.message || "Download failed. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  return {
    isDownloading,
    downloadEvidenceFiles,
    downloadError,
  };
};
